/**
 * タイトル画面 / 設定画面（ポーズ）/ 章クリア画面。
 */

import type { Chapter, KnowledgeCard } from '../types/scenario';
import { SETTINGS_RANGES, type GameSettings } from '../engine/settings';
import { button, clear, el, sliderRow, toggleRow } from './dom';
import { usesTouchControls } from './touchControls';

export interface TitleHooks {
  onStart: (playerName: string) => void;
  onContinue: () => void;
  onOpenSettings: () => void;
  hasSave: () => boolean;
}

export class TitleScreen {
  readonly root: HTMLElement;
  private nameInput: HTMLInputElement;
  private continueButton: HTMLButtonElement;

  constructor(private readonly hooks: TitleHooks) {
    this.nameInput = el('input', {
      class: 'title-input',
      type: 'text',
      maxlength: 12,
      placeholder: 'あなたの名前（省略可）',
    });
    this.continueButton = button('つづきから', () => this.hooks.onContinue(), 'btn btn-wide');

    this.root = el('div', { class: 'title-layer' }, [
      el('div', { class: 'title-panel' }, [
        el('h1', { class: 'title-main', text: 'NetOps Rookie' }),
        el('p', { class: 'title-sub', text: 'ネットオプス・ルーキー' }),
        el('p', { class: 'title-tag', text: '「つながらない」を、つなぎなおせ。' }),
        el('div', { class: 'title-form' }, [
          el('label', { class: 'title-label', text: '主人公の名前' }),
          this.nameInput,
        ]),
        el('div', { class: 'title-buttons' }, [
          button(
            'はじめから',
            () => this.hooks.onStart(this.nameInput.value.trim() || '新人'),
            'btn btn-wide btn-primary',
          ),
          this.continueButton,
          button('設定', () => this.hooks.onOpenSettings(), 'btn btn-wide'),
        ]),
        el('p', {
          class: 'title-note',
          text: usesTouchControls()
            ? '下部中央のパッドは上で前進、下で後退、左右で旋回。斜めで移動と旋回を同時に操作できます。中央の点を対象に向けて「調べる・話す」をタップ。'
            : '操作：WASD・矢印で移動 / マウス・Shift＋矢印で視点 / Shift＋WASDでダッシュ / E で調べる・話す / Tab で手帳 / T で端末 / R で電波スキャナー / M でトラフィックモニター / Esc でメニュー',
        }),
        el('p', {
          class: 'title-note',
          text: '3D 酔いが気になる方は、設定で視野角・感度・ヘッドボブを調整できます。',
        }),
      ]),
    ]);
  }

  show(): void {
    this.root.hidden = false;
    this.continueButton.disabled = !this.hooks.hasSave();
    this.continueButton.classList.toggle('disabled', this.continueButton.disabled);
  }

  hide(): void {
    this.root.hidden = true;
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }
}

export interface SettingsHooks {
  onChange: (settings: GameSettings) => void;
  onClose: () => void;
  onSave: () => void;
  onReturnToTitle: () => void;
}

export class SettingsMenu {
  readonly root: HTMLElement;
  private body: HTMLElement;
  private settings: GameSettings;
  private inGame = true;

  constructor(initial: GameSettings, private readonly hooks: SettingsHooks) {
    this.settings = { ...initial, volumes: { ...initial.volumes } };
    this.body = el('div', { class: 'menu-body' });
    this.root = el('div', { class: 'menu-layer', hidden: true }, [
      el('div', { class: 'menu-panel' }, [
        el('div', { class: 'menu-head' }, [el('span', { class: 'menu-title', text: 'メニュー' })]),
        this.body,
      ]),
    ]);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  setSettings(settings: GameSettings): void {
    this.settings = { ...settings, volumes: { ...settings.volumes } };
  }

  open(inGame: boolean): void {
    this.inGame = inGame;
    this.root.hidden = false;
    this.render();
  }

  close(): void {
    this.root.hidden = true;
  }

  private update(patch: Partial<GameSettings>): void {
    this.settings = { ...this.settings, ...patch };
    this.hooks.onChange(this.settings);
  }

  private updateVolume(patch: Partial<GameSettings['volumes']>): void {
    this.settings = { ...this.settings, volumes: { ...this.settings.volumes, ...patch } };
    this.hooks.onChange(this.settings);
  }

  private render(): void {
    clear(this.body);
    const percent = (v: number): string => `${Math.round(v * 100)}%`;

    this.body.append(el('h3', { class: 'menu-h', text: '表示・操作（酔い対策）' }));
    this.body.append(
      sliderRow(
        '視野角',
        this.settings.fov,
        SETTINGS_RANGES.fov,
        (v) => this.update({ fov: v }),
        (v) => `${Math.round(v)}°`,
      ),
    );
    this.body.append(
      sliderRow(
        '視点の感度',
        this.settings.mouseSensitivity,
        SETTINGS_RANGES.mouseSensitivity,
        (v) => this.update({ mouseSensitivity: v }),
        (v) => v.toFixed(1),
      ),
    );
    this.body.append(
      sliderRow(
        '視点のなめらかさ',
        this.settings.viewSmoothing,
        SETTINGS_RANGES.viewSmoothing,
        (v) => this.update({ viewSmoothing: v }),
        (v) => (v <= 0.001 ? '切' : v.toFixed(2)),
      ),
    );
    this.body.append(
      toggleRow('ヘッドボブ（歩行時の揺れ）', this.settings.headBob, (v) =>
        this.update({ headBob: v }),
      ),
    );

    this.body.append(el('h3', { class: 'menu-h', text: '音量' }));
    const volumeRange = { min: 0, max: 1, step: 0.05 };
    this.body.append(
      sliderRow('マスター', this.settings.volumes.master, volumeRange, (v) =>
        this.updateVolume({ master: v }), percent),
    );
    this.body.append(
      sliderRow('BGM', this.settings.volumes.bgm, volumeRange, (v) =>
        this.updateVolume({ bgm: v }), percent),
    );
    this.body.append(
      sliderRow('効果音', this.settings.volumes.sfx, volumeRange, (v) =>
        this.updateVolume({ sfx: v }), percent),
    );
    this.body.append(
      sliderRow('環境音', this.settings.volumes.ambient, volumeRange, (v) =>
        this.updateVolume({ ambient: v }), percent),
    );

    const actions = el('div', { class: 'menu-actions' });
    actions.append(button('閉じる', () => this.hooks.onClose(), 'btn btn-primary'));
    if (this.inGame) {
      actions.append(button('セーブ', () => this.hooks.onSave(), 'btn'));
      actions.append(button('タイトルへ戻る', () => this.hooks.onReturnToTitle(), 'btn btn-ghost'));
    }
    this.body.append(actions);
  }
}

export interface ChapterClearHooks {
  onNext: () => void;
}

export class ChapterClearScreen {
  readonly root: HTMLElement;
  private body: HTMLElement;

  constructor(private readonly hooks: ChapterClearHooks) {
    this.body = el('div', { class: 'clear-body' });
    this.root = el('div', { class: 'clear-layer', hidden: true }, [
      el('div', { class: 'clear-panel' }, [
        el('div', { class: 'clear-head', text: 'CHAPTER CLEAR' }),
        this.body,
      ]),
    ]);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  show(
    chapter: Chapter,
    cards: KnowledgeCard[],
    stats: { wrongHypotheses: number; hintsUsed: number; elapsedSec: number } | undefined,
    hasNext: boolean,
  ): void {
    clear(this.body);
    this.body.append(
      el('h2', { class: 'clear-title', text: `第${chapter.number}章\u3000${chapter.title}` }),
    );

    this.body.append(el('h3', { class: 'clear-h', text: '今回できるようになったこと' }));
    const list = el('ul', { class: 'clear-list' });
    for (const objective of chapter.objectives) {
      list.append(el('li', { text: objective.text }));
    }
    this.body.append(list);

    if (cards.length > 0) {
      this.body.append(el('h3', { class: 'clear-h', text: '獲得した知識カード' }));
      for (const card of cards) {
        this.body.append(
          el('div', { class: 'clear-card' }, [
            el('div', { class: 'clear-card-term', text: card.term }),
            el('div', { class: 'clear-card-analogy', text: `たとえるなら：${card.analogy}` }),
            el('div', { class: 'clear-card-summary', text: card.summary }),
            card.realWorld
              ? el('div', { class: 'clear-card-real', text: `実生活では：${card.realWorld}` })
              : null,
          ]),
        );
      }
    }

    if (stats) {
      const minutes = Math.floor(stats.elapsedSec / 60);
      const seconds = stats.elapsedSec % 60;
      this.body.append(
        el('p', {
          class: 'clear-stats',
          text: `記録：所要時間 ${minutes}分${seconds}秒 ／ 外れた仮説 ${stats.wrongHypotheses} 回 ／ ヒント ${stats.hintsUsed} 回`,
        }),
      );
      this.body.append(
        el('p', {
          class: 'clear-note',
          text: 'この数字は評価ではありません。「早く直す」より「正しく切り分けて、説明できる」ことのほうが大切です。',
        }),
      );
    }

    this.body.append(
      el('div', { class: 'menu-actions' }, [
        button(hasNext ? '次の章へ' : 'タイトルへ戻る', () => this.hooks.onNext(), 'btn btn-primary btn-wide'),
      ]),
    );
    this.root.hidden = false;
  }

  hide(): void {
    this.root.hidden = true;
  }
}
